import {createSelector} from '@reduxjs/toolkit'
import {
  getAnchorCity,
  getAnchorDisplayName,
  getHotel,
  getHotelIdsToDisplay
} from 'modules/sapiSearch/selectors'
import {getSearchParameters} from 'modules/sapiSearch/selectors/searchParameters'
import {getSearchBoxValues} from 'modules/searchBox/selectors'

import {
  numberOfGuests,
  roomsStringToRoomsSplit
} from '@vio/core/src/room/business/roomConfiguration'
import {searchApi} from '@vio/core/src/sapi/services/searchApi'

import type {ServerContext} from '../hooks/useChatStream'
import type {ChatFocus, ChatView} from './slice'
import type {RootState} from 'store'

const DEFAULT_SUGGESTED_QUESTIONS = [
  'Find hotels in Amsterdam for next weekend',
  'When is my next hotel booking?',
  'Show me the price trends'
]

const HOTEL_SUGGESTED_QUESTIONS = [
  'What is the checkout time?',
  'Are pets allowed?',
  'How far is the nearest metro station?'
]

const SEARCH_SUGGESTED_QUESTIONS = [
  "What's the highest rated hotel?",
  'Which hotels have free parking?'
]

/** Resolve hotel name from sapiSearch entities or RTKQ cache */
function getHotelName(state: RootState, hotelId: string): string | undefined {
  return (
    getHotel(state, hotelId)?.hotelName ??
    searchApi.endpoints.getHotel.select({hotelId})(state)?.data?.hotelName
  )
}

const getChatState = (state: RootState) => state.chat

export const getIsChatOpen = (state: RootState): boolean => state.chat.isOpen

export const getChatView = (state: RootState): ChatView => state.chat.view

export const getChatSessionId = (state: RootState): string | null =>
  state.chat.sessionId

export const getChatFocus = (state: RootState): ChatFocus | null =>
  state.chat.focus ?? state.chat.pageFocus

/** Hotel name derived from focus — avoids passing full state into createSelector */
const getFocusedHotelName = (state: RootState): string | undefined => {
  const focus = getChatFocus(state)
  if (!focus || focus.type !== 'hotel') return undefined

  return getHotelName(state, focus.hotelId)
}

const getSuggestedQuestions = createSelector(
  getChatFocus,
  (focus): string[] => {
    if (!focus) return DEFAULT_SUGGESTED_QUESTIONS

    return focus.type === 'hotel'
      ? HOTEL_SUGGESTED_QUESTIONS
      : SEARCH_SUGGESTED_QUESTIONS
  }
)

export const getRemainingSuggestions = createSelector(
  getSuggestedQuestions,
  getChatState,
  (questions, chat): string[] =>
    questions.filter(q => !chat.sentSuggestions.includes(q))
)

export const getWelcomeMessage = createSelector(
  getChatFocus,
  (focus): string | undefined => {
    if (focus) return undefined
    return 'How can I help you today?'
  }
)

function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})
}

export const getChatFocusLabel = createSelector(
  getChatFocus,
  getFocusedHotelName,
  getSearchBoxValues,
  getAnchorDisplayName,
  getAnchorCity,
  (
    focus,
    hotelName,
    searchBoxValues,
    anchorName,
    anchorCity
  ): string | undefined => {
    if (!focus) return undefined

    if (focus.type === 'hotel') {
      return hotelName ?? 'Hotel'
    }

    // Build a search summary: "Amsterdam · Mar 15–17 · 2 guests"
    const parts: string[] = []
    const placeName = anchorName || anchorCity
    if (placeName) parts.push(placeName)

    const {checkIn, checkOut, rooms} = searchBoxValues
    if (checkIn && checkOut) {
      parts.push(`${formatDateShort(checkIn)}–${formatDateShort(checkOut)}`)
    }

    if (typeof rooms === 'string' && rooms) {
      const guests = numberOfGuests(rooms)
      if (guests > 0) parts.push(`${guests} guest${guests !== 1 ? 's' : ''}`)
    }

    return parts.length > 0 ? parts.join(' · ') : 'Search'
  }
)

export const getServerContext = createSelector(
  getChatFocus,
  getSearchParameters,
  getHotelIdsToDisplay,
  getAnchorDisplayName,
  getAnchorCity,
  (
    focus,
    searchParameters,
    hotelIdsToDisplay,
    anchorDisplayName,
    anchorCity
  ): ServerContext => {
    const serverContext: ServerContext = {}

    if (focus?.type === 'hotel') {
      serverContext.focus = {hotelId: focus.hotelId}
    }

    const search: Record<string, unknown> = {}
    const inputs: Record<string, unknown> = {}
    if (searchParameters.hotelId)
      inputs.anchorHotelId = searchParameters.hotelId
    if (searchParameters.placeId) inputs.placeId = searchParameters.placeId
    if (searchParameters.checkIn) inputs.checkIn = searchParameters.checkIn
    if (searchParameters.checkOut) inputs.checkOut = searchParameters.checkOut
    if (searchParameters.rooms) {
      inputs.rooms = roomsStringToRoomsSplit(searchParameters.rooms)
    }
    if (searchParameters.optimizeRooms !== undefined)
      inputs.optimizeRooms = searchParameters.optimizeRooms
    if (searchParameters.boundingBox) {
      const [north, east, south, west] = searchParameters.boundingBox
      inputs.boundingBox = {
        topRight: {latitude: north, longitude: east},
        bottomLeft: {latitude: south, longitude: west}
      }
    }
    if (searchParameters.geolocation) {
      const {lat, lon} = searchParameters.geolocation
      inputs.geolocation = {latitude: lat, longitude: lon}
    }
    if (searchParameters.filters) inputs.filters = searchParameters.filters
    const query = anchorDisplayName || anchorCity
    if (query) inputs.query = query
    if (Object.keys(inputs).length > 0) search.inputs = inputs

    if (hotelIdsToDisplay?.length) {
      search.outputs = {hotelIds: hotelIdsToDisplay}
    }

    if (Object.keys(search).length > 0) {
      serverContext.search = JSON.stringify(search)
    }

    return serverContext
  }
)
